// The co-op INVITATION pop-up (and the small "they answered" notices). Mounted once for the whole site: it appears on any page,
// as soon as the player signs in, and again whenever a new invitation arrives (src/game/inviteManager.js polls the server).
//   • a waiting invitation: who invited, the terms, a live countdown, [Accept] [Decline] [Decide later] [Block this player]
//   • an invitation that ran out before the player signed in: told once that it was received and expired
// Raid / trip modals (z-index 9100) stay above this one.

import './invites.css'
import { Events, on } from './eventBus.js'
import { attention, inviteActions, getInvites, refresh } from '../game/inviteManager.js'
import { canTravelNow } from '../game/coopManager.js'
import { CoopConfig } from '../config/balance.js'

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
export const regNo = (n) => (n == null ? '—' : String(n).padStart(6, '0'))
// (a stay is never longer than a day: older invitations with a longer or open-ended stay are cut to that)
export const STAY_LABEL = (h) => { const eff = h > 0 && h <= CoopConfig.maxStayHours ? h : CoopConfig.maxStayHours; return eff % 24 === 0 ? `up to ${eff / 24} day${eff === 24 ? '' : 's'}` : `up to ${eff} hour${eff === 1 ? '' : 's'}` }
export function countdown(ms) {
  if (ms <= 0) return 'now'
  const m = Math.floor(ms / 60000), d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60)
  return d ? `${d} d ${h} h` : h ? `${h} h ${m % 60} m` : `${Math.max(1, m)} m`
}
const when = (t) => new Date(t).toLocaleString(undefined, { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit' })

export function mountInvitePopup() {
  const layer = document.createElement('div')
  layer.className = 'inv-layer'
  document.body.appendChild(layer)
  let showing = null   // the invitation currently on screen
  let busy = false
  let clock = null
  const toasts = []

  function toast(text) {
    const el = document.createElement('div'); el.className = 'inv-toast'; el.setAttribute('role', 'status'); el.textContent = text
    layer.appendChild(el); toasts.push(el)
    setTimeout(() => { el.classList.add('out'); setTimeout(() => el.remove(), 400) }, 7000)
  }

  function close() { clearInterval(clock); clock = null; showing = null; layer.querySelector('.inv-pop')?.remove(); next() }

  function render(inv, note = null) {
    clearInterval(clock)
    layer.querySelector('.inv-pop')?.remove()
    const more = attention().filter((i) => i.id !== inv.id).length
    const expired = inv.status === 'expired'
    const h = inv.host
    const el = document.createElement('div')
    el.className = 'inv-pop'
    el.innerHTML = `
      <div class="inv-card" role="dialog" aria-modal="true" aria-labelledby="inv-title">
        <div class="inv-kicker">${expired ? 'Invitation expired' : 'You are invited'}</div>
        <h2 id="inv-title">${expired ? `${esc(h.name)} invited you, but the invitation has expired.` : `${esc(h.name)} invites you to join their kingdom.`}</h2>
        <p class="inv-from">No. ${regNo(h.registryNo)} · ${esc(h.kingdom ?? '')} · ${esc(h.title ?? '')}</p>
        ${note ? `<p class="inv-note inv-note--done" role="status">${esc(note)}</p>` : expired ? `<p>It was sent on ${esc(when(inv.createdAt))} and ended on ${esc(when(inv.expiresAt))}. You did not miss anything else — ${esc(h.name)} can invite you again.</p>` : `
        <dl class="inv-terms">
          <div><dt>Offer ends in</dt><dd data-inv-left>${esc(countdown(inv.expiresAt - Date.now()))}</dd></div>
          <div><dt>You may stay</dt><dd>${esc(STAY_LABEL(inv.stayHours))}</dd></div>
          <div><dt>Your wage</dt><dd>${inv.wagePct}% of what you earn together</dd></div>
        </dl>
        <p class="inv-note">While you visit, your own kingdom keeps running — goons can still attack your home, just as on an adventure.</p>`}
        <p class="inv-error" data-inv-error role="alert" hidden></p>
        <div class="inv-actions">
          ${note ? '<button type="button" class="inv-btn inv-btn--main" data-inv="close">Close</button>'
            : expired ? '<button type="button" class="inv-btn inv-btn--main" data-inv="ok">OK</button>'
            : `<button type="button" class="inv-btn inv-btn--main" data-inv="accept">Accept</button>
               <button type="button" class="inv-btn" data-inv="decline">Decline</button>
               <button type="button" class="inv-btn inv-btn--quiet" data-inv="later">Decide later</button>
               <button type="button" class="inv-btn inv-btn--quiet inv-btn--danger" data-inv="block">Block this player</button>`}
        </div>
        ${more ? `<p class="inv-more">${more} more invitation${more === 1 ? '' : 's'} waiting.</p>` : ''}
      </div>`
    layer.appendChild(el)
    el.querySelector('[data-inv]')?.focus()
    if (!expired && !note) clock = setInterval(() => { const l = el.querySelector('[data-inv-left]'); if (l) l.textContent = countdown(inv.expiresAt - Date.now()); if (inv.expiresAt <= Date.now()) { clearInterval(clock); refreshSoon() } }, 1000)
  }
  const refreshSoon = () => refresh()

  let retry = null
  function next() {
    if (showing) return
    const list = attention()
    if (!list.length) return
    if (document.querySelector('.inv-pop')) { clearTimeout(retry); retry = setTimeout(next, 1200); return } // a proposal (or another dialog) is up: wait our turn
    showing = list[0]
    render(showing)
  }

  async function run(fn, doneNote) {
    if (busy || !showing) return
    busy = true
    const err = layer.querySelector('[data-inv-error]')
    try { await fn(); if (doneNote) showing && render(showing, doneNote); else close() } catch (e) {
      const msg = e?.body?.error ?? 'Something went wrong — try again.'
      if (err) { err.textContent = msg; err.hidden = false }
      if (e?.status === 410 || e?.status === 409) { showing = null; setTimeout(() => { layer.querySelector('.inv-pop')?.remove(); next() }, 2500) }
    } finally { busy = false }
  }

  layer.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-inv]'); if (!b || !showing) return
    const inv = showing
    switch (b.dataset.inv) {
      case 'accept': {
        const can = canTravelNow()
        if (!can.ok) { const err = layer.querySelector('[data-inv-error]'); if (err) { err.textContent = can.reason; err.hidden = false } break }
        await run(() => inviteActions.accept(inv.id), `You accepted. Travelling to ${inv.host.name}'s kingdom…`)
        setTimeout(() => { if (layer.querySelector('.inv-note--done')) close() }, 1600) // (the travel screen takes over)
        break
      }
      case 'decline': run(() => inviteActions.decline(inv.id), null); break
      case 'block': if (confirm(`Block ${inv.host.name}? You will not receive invitations from them.`)) run(() => inviteActions.block(inv.hostId), null); break
      case 'later': await inviteActions.dismiss(inv); close(); break
      case 'ok': await inviteActions.dismiss(inv); close(); break
      case 'close': close(); break
      default:
    }
  })
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && showing && layer.querySelector('.inv-pop')) { const inv = showing; inviteActions.dismiss(inv); close() } })

  on(Events.InvitesChange, ({ attention: att, changedSent }) => {
    for (const i of changedSent ?? []) toast(`${i.guestName ?? 'The player'} ${i.status === 'accepted' ? 'accepted' : 'declined'} your invitation.`)
    if (showing && !att.some((i) => i.id === showing.id) && !layer.querySelector('.inv-note--done') && !busy) { showing = null; layer.querySelector('.inv-pop')?.remove() } // answered elsewhere / cancelled / expired meanwhile
    if (!showing) next()
  })
  if (getInvites().loaded) next()
  return { toast }
}
